import { useEffect, useState } from 'react';

import { Alert, Snackbar } from '@mui/material';
import type { SnackbarCloseReason } from '@mui/material';

import { setGlobalFeedbackListener } from './globalFeedbackBus';
import type { GlobalFeedbackMessage } from './globalFeedbackBus';

/**
 * App-shell-level surface for {@link publishGlobalFeedback} calls (mount exactly
 * once — `AppLayout` does this — since it registers itself as the single global
 * listener). See `globalFeedbackBus.ts` for why this exists alongside the
 * per-feature `useFeedback`/`useToast` + local `<Snackbar>` pattern.
 */
export function GlobalFeedbackSnackbar() {
  const [feedback, setFeedback] = useState<GlobalFeedbackMessage | null>(null);

  useEffect(() => {
    setGlobalFeedbackListener(setFeedback);
    return () => setGlobalFeedbackListener(null);
  }, []);

  const handleClose = (_event: unknown, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    setFeedback(null);
  };

  return (
    <Snackbar
      open={feedback !== null}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setFeedback(null)}
        severity={feedback?.severity ?? 'info'}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {feedback?.message ?? ''}
      </Alert>
    </Snackbar>
  );
}
